/**
 * Onboarding Store
 *
 * 온보딩 진행 상태 관리
 * 기존 OnboardingContext를 대체합니다.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ============================================================================
// Types
// ============================================================================

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  targetSelector?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  route?: string;
}

interface OnboardingState {
  isActive: boolean;
  currentStepIndex: number;
  completedSteps: string[];
  hasSeenWelcome: boolean;
  isWelcomeOpen: boolean;
}

interface OnboardingActions {
  // Actions
  startOnboarding: () => void;
  stopOnboarding: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
  completeStep: (stepId: string) => void;
  showWelcome: () => void;
  hideWelcome: () => void;
  resetOnboarding: () => void;

  // Helpers
  isStepCompleted: (stepId: string) => boolean;
  getProgress: () => number;
}

type OnboardingStore = OnboardingState & OnboardingActions;

// ============================================================================
// Constants
// ============================================================================

// 채널 중심 IA 기반 5단계 온보딩 (Phase 1A 이후)
export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'channel-hub',
    title: '채널 운영 허브',
    description:
      '이곳이 여러분의 채널 홈입니다. 진행 중인 영상, 다음 업로드, 최근 플레이리스트를 한눈에 확인하세요.',
    targetSelector: '[data-onboarding="channel-hub"]',
    placement: 'bottom',
    route: '/channels',
  },
  {
    id: 'create-first-video',
    title: '첫 영상 만들기',
    description:
      '"새 영상 만들기" 버튼을 클릭해보세요. AI가 테마에 맞는 음악을 자동 생성하고 YouTube 업로드까지 도와드립니다.',
    targetSelector: '[data-onboarding="create-playlist-btn"]',
    placement: 'bottom',
    route: '/channels',
  },
  {
    id: 'playlist-tracks',
    title: '음악 생성 & 관리',
    description:
      '플레이리스트 상세에서 각 트랙의 여러 버전(A/B/C)을 비교하고 마음에 드는 버전을 선택하세요. 생성 중에는 화면 상단 배너에서 진행 상황을 확인할 수 있습니다.',
    targetSelector: '[data-onboarding="track-list"]',
    placement: 'top',
    route: '/playlists',
  },
  {
    id: 'youtube-upload',
    title: 'YouTube 업로드',
    description:
      '플레이리스트 상세의 "배포" 탭에서 원클릭으로 YouTube 채널에 업로드하세요. 공개 예약도 설정할 수 있습니다.',
    targetSelector: '[data-onboarding="deploy-tab"]',
    placement: 'bottom',
    route: '/playlists',
  },
  {
    id: 'upload-schedule',
    title: '업로드 일정 관리',
    description:
      '채널 설정의 "업로드 스케줄"에서 정기 업로드를 예약하세요. AI Agent와 함께 사용하면 채널을 완전 자동으로 운영할 수 있습니다.',
    targetSelector: '[data-onboarding="upload-schedule"]',
    placement: 'top',
    route: '/channels',
  },
];

// ============================================================================
// Initial State
// ============================================================================

const initialState: OnboardingState = {
  isActive: false,
  currentStepIndex: 0,
  completedSteps: [],
  hasSeenWelcome: false,
  isWelcomeOpen: false,
};

// ============================================================================
// Store
// ============================================================================

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      startOnboarding: () => set({ isActive: true, currentStepIndex: 0 }),

      stopOnboarding: () => set({ isActive: false }),

      nextStep: () =>
        set((state) => ({
          currentStepIndex: Math.min(state.currentStepIndex + 1, ONBOARDING_STEPS.length - 1),
        })),

      prevStep: () =>
        set((state) => ({
          currentStepIndex: Math.max(state.currentStepIndex - 1, 0),
        })),

      goToStep: (index) =>
        set({
          currentStepIndex: Math.max(0, Math.min(index, ONBOARDING_STEPS.length - 1)),
        }),

      completeStep: (stepId) =>
        set((state) => {
          if (state.completedSteps.includes(stepId)) {
            return state;
          }
          return { completedSteps: [...state.completedSteps, stepId] };
        }),

      showWelcome: () => set({ isWelcomeOpen: true }),

      hideWelcome: () => set({ isWelcomeOpen: false, hasSeenWelcome: true }),

      resetOnboarding: () => set(initialState),

      isStepCompleted: (stepId) => get().completedSteps.includes(stepId),

      getProgress: () => {
        const { completedSteps } = get();
        if (ONBOARDING_STEPS.length === 0) {
          return 0;
        }
        return (completedSteps.length / ONBOARDING_STEPS.length) * 100;
      },
    }),
    {
      name: 'onboarding-storage',
      partialize: (state) => ({
        completedSteps: state.completedSteps,
        hasSeenWelcome: state.hasSeenWelcome,
      }),
    }
  )
);

// ============================================================================
// Hook (기존 useOnboarding 호환)
// ============================================================================

/**
 * useOnboarding 훅
 *
 * 기존 OnboardingContext의 useOnboarding과 동일한 API를 제공합니다.
 *
 * @example
 * const { state, currentStep, nextStep, prevStep } = useOnboarding();
 */
export function useOnboarding() {
  const store = useOnboardingStore();

  const currentStep = store.isActive ? ONBOARDING_STEPS[store.currentStepIndex] : null;

  return {
    state: {
      isActive: store.isActive,
      currentStepIndex: store.currentStepIndex,
      completedSteps: store.completedSteps,
      hasSeenWelcome: store.hasSeenWelcome,
      isWelcomeOpen: store.isWelcomeOpen,
    },
    steps: ONBOARDING_STEPS,
    currentStep,
    startOnboarding: store.startOnboarding,
    stopOnboarding: store.stopOnboarding,
    nextStep: store.nextStep,
    prevStep: store.prevStep,
    goToStep: store.goToStep,
    completeStep: store.completeStep,
    showWelcome: store.showWelcome,
    hideWelcome: store.hideWelcome,
    resetOnboarding: store.resetOnboarding,
    isStepCompleted: store.isStepCompleted,
    getProgress: store.getProgress,
  };
}

/**
 * useOnboardingOptional 훅
 *
 * Provider 없이도 사용 가능한 버전 (항상 정의됨)
 */
export function useOnboardingOptional() {
  return useOnboarding();
}

// ============================================================================
// Selectors
// ============================================================================

export const selectIsActive = (state: OnboardingStore) => state.isActive;
export const selectCurrentStepIndex = (state: OnboardingStore) => state.currentStepIndex;
export const selectHasSeenWelcome = (state: OnboardingStore) => state.hasSeenWelcome;
export const selectIsWelcomeOpen = (state: OnboardingStore) => state.isWelcomeOpen;
export const selectProgress = (state: OnboardingStore) => {
  // PR-5f: getProgress와 동일하게 length 0 가드
  if (ONBOARDING_STEPS.length === 0) {
    return 0;
  }
  return (state.completedSteps.length / ONBOARDING_STEPS.length) * 100;
};

/**
 * 현재 단계 가져오기 (Store 외부에서 호출 가능)
 */
export function getCurrentStep(): OnboardingStep | null {
  const state = useOnboardingStore.getState();
  return state.isActive ? ONBOARDING_STEPS[state.currentStepIndex] : null;
}
